import {
  ATTR_KEYS,
  attrSignature,
  attrsEqualKey,
  compareCollapsedRows,
  cptCodeAttrsFromRecord,
  normalizeDescriptionKey,
  sortFlaggedKeys,
} from './comparing-attributes';
import { toArray } from './normalizing-facet';

function emptyCollapsedRow(row) {
  return {
    cptCodeRateIds: [],
    attrs: { ...row.attrs },
    docCount: 0,
    flagged: new Set(),
  };
}

function addRowToCollapsedBucket(bucket, row) {
  if (!bucket.cptCodeRateIds.includes(row.id)) {
    bucket.cptCodeRateIds.push(row.id);
  }

  bucket.docCount = Math.max(bucket.docCount, row.docCount);
}

function finalizeCollapsedBucket(bucket) {
  return {
    cptCodeRateIds: [...new Set(bucket.cptCodeRateIds)].sort((left, right) =>
      String(left).localeCompare(String(right), undefined, { numeric: true })
    ),
    attrs: bucket.attrs,
    docCount: bucket.docCount,
    flagged: bucket.flagged,
  };
}

function collapseRowsWithDocCounts(rows) {
  let bySignature = new Map();

  for (let row of rows) {
    let signature = attrSignature(row.attrs);
    if (!bySignature.has(signature)) {
      bySignature.set(signature, emptyCollapsedRow(row));
    }

    addRowToCollapsedBucket(bySignature.get(signature), row);
  }

  return [...bySignature.values()].map(finalizeCollapsedBucket);
}

function groupCollapsedBucketsByDescription(buckets) {
  let byDescription = new Map();

  for (let bucket of buckets) {
    let descriptionKey = normalizeDescriptionKey(bucket.attrs.description);
    if (!byDescription.has(descriptionKey)) {
      byDescription.set(descriptionKey, []);
    }
    byDescription.get(descriptionKey).push(bucket);
  }

  return byDescription;
}

function differingAttrKeys(left, right) {
  let keys = new Set();

  for (let key of ATTR_KEYS) {
    if (!attrsEqualKey(key, left.attrs[key], right.attrs[key])) {
      keys.add(key);
    }
  }

  return keys;
}

function addDifferingKeys(union, left, right) {
  for (let key of differingAttrKeys(left, right)) {
    union.add(key);
  }
}

function familyFlagKeys(family) {
  let union = new Set();

  for (let i = 0; i < family.length; i++) {
    for (let j = i + 1; j < family.length; j++) {
      addDifferingKeys(union, family[i], family[j]);
    }
  }

  return union;
}

function flagFamilyBuckets(family, flagKeys) {
  for (let bucket of family) {
    for (let key of flagKeys) {
      bucket.flagged.add(key);
    }
  }
}

function applySameDescriptionFamilyFlags(buckets) {
  for (let family of groupCollapsedBucketsByDescription(buckets).values()) {
    if (family.length < 2) {
      continue;
    }

    flagFamilyBuckets(family, familyFlagKeys(family));
  }
}

function rowFromCptCodeRecord(code, docCountByKey) {
  return {
    id: code.id,
    attrs: cptCodeAttrsFromRecord(code),
    docCount: docCountByKey.get(code.id)?.docCount ?? 0,
  };
}

function docCountForRateKeys(rateIds, docCountByKey) {
  let max = 0;

  for (let id of rateIds) {
    max = Math.max(
      max,
      Number(docCountByKey.get(id)?.docCount ?? docCountByKey.get(String(id))?.docCount ?? 0)
    );
  }

  return max;
}

export function buildCollapsedBucketsFromCodes(codes, docCountByKey) {
  let rows = toArray(codes).map(code => rowFromCptCodeRecord(code, docCountByKey));

  if (!rows.length) {
    return [];
  }

  let collapsed = collapseRowsWithDocCounts(rows);
  applySameDescriptionFamilyFlags(collapsed);
  collapsed.sort(compareCollapsedRows);

  return collapsed;
}

function serviceRateFromCollapsedBucket(bucket, { allRateIds, docCount } = {}) {
  let rateIds = allRateIds ?? bucket.cptCodeRateIds;
  return {
    key: rateIds[0],
    allRateIds: rateIds,
    docCount: docCount ?? bucket.docCount,
    duration: bucket.attrs.duration,
    rate: bucket.attrs.rate,
    description: bucket.attrs.description,
    callToBook: bucket.attrs.callToBook,
    requireCreditCardForNewClients: bucket.attrs.requireCreditCardForNewClients,
    flagged: sortFlaggedKeys([...bucket.flagged]),
  };
}

function bucketsToServiceGroups(sortedBuckets, { docCountByKey } = {}) {
  let rates = sortedBuckets.map(bucket => {
    let allRateIds = bucket.cptCodeRateIds;
    let docCount = docCountForRateKeys(allRateIds, docCountByKey);

    return serviceRateFromCollapsedBucket(bucket, { allRateIds, docCount });
  });

  return [
    {
      heading: null,
      rates,
      cptCodeRateIds: rates.flatMap(rate => rate.allRateIds),
    },
  ];
}

export function buildServiceGroupsFromCodes(codes, docCountByKey) {
  let collapsed = buildCollapsedBucketsFromCodes(codes, docCountByKey);
  if (!collapsed.length) {
    return null;
  }

  return bucketsToServiceGroups(collapsed, { docCountByKey });
}

function fallbackRateFromBucket(bucket) {
  return {
    key: bucket.key,
    allRateIds: [bucket.key],
    docCount: Number(bucket.docCount ?? 0),
    duration: undefined,
    rate: undefined,
    description: null,
    callToBook: undefined,
    requireCreditCardForNewClients: undefined,
    flagged: [],
  };
}

export function groupsFromBucketsWithoutCptMetadata(list) {
  return list.map(bucket => ({
    heading: null,
    cptCodeRateIds: [bucket.key],
    rates: [fallbackRateFromBucket(bucket)],
  }));
}
